import type {
  OrchestratedAuctionListingDetails,
  OrchestratedLotWithGroups,
  OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  getAccountsClient,
  getAuctionWithBid,
  fetchAllResults,
  getGroupsClient,
  sanitizeAccessToken,
  getLotsClient,
  augmentLots,
  makeOptionallyAuthenticatedQuery,
  augmentOfferingItems,
  fetchOfferingsByListingIds,
  getOffersClient,
  getStorefrontsClient,
  getSellerInfo,
  fetchContractById,
  getListing,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { ItemDto } from '@b-stock/listing-api-client'
import { ItemDtoStatusEnum } from '@b-stock/listing-api-client'

import type { CombinedListingSearchResult } from './CombinedListingSearchResult'
import {
  buildTermsPromises,
  type ListingDetails,
  type ListingDetailsCommon,
} from './listingDetailsQuery'

/**
 * searchListingDetailsQuery is a faster version of listingDetailsQuery for use
 * in inline bidding where we have searchListing and then load listing details.
 *
 * Starting with a searchListing, we can build a result in 2 steps, vs 3 steps
 * in listingDetailsQuery.
 */
const getSearchListingDetails = async (
  accessToken: string | null,
  searchListing: CombinedListingSearchResult,
  accountId?: string | null
): Promise<ListingDetails> => {
  const accountsClient = getAccountsClient()
  const groupsClient = getGroupsClient()
  const lotsClient = getLotsClient()
  const storefrontsClient = getStorefrontsClient()
  const offersClient = getOffersClient()
  const reqOpts = buildAxiosRequestConfig({ accessToken })

  const {
    deal = null,
    listingId: id,
    lotId,
    offeringId,
    pricingStrategy,
    problemFreeOrderRate = null,
    sellerId,
    storefrontId,
    typicalSellingPrice = null,
  } = searchListing

  const isAuctionListing = pricingStrategy === 'AUCTION'

  if (!lotId) {
    throw new Error('No lot associated with this listing, this is unexpected.')
  }

  /**
   * Step 1: fetches based on function props
   */
  const listingPromise = getListing(id, accessToken, false)

  const lotPromise = lotsClient.findOneLot({ id: lotId }, reqOpts)

  const auctionPromise = isAuctionListing
    ? getAuctionWithBid({
        accessToken,
        accountId,
        listingId: id,
      })
    : null

  const offeringsPromise =
    accessToken && !isAuctionListing
      ? fetchOfferingsByListingIds([id], accessToken)
      : null

  const offersPromise =
    offeringId && accessToken && !isAuctionListing
      ? offersClient.findOffers({ offeringId: [offeringId] }, reqOpts)
      : null

  // For auctions, we fetch a 6-item preview
  const auctionItemsPromise = isAuctionListing
    ? lotsClient.findItemsByLot({ lotId, limit: 6 }, reqOpts)
    : null

  // For offerings, we need to fetch the full item set
  const offeringItemsPromise = isAuctionListing
    ? null
    : fetchAllResults({
        apiClient: lotsClient,
        query: lotsClient.findItemsByLot,
        params: { lotId, status: [ItemDtoStatusEnum.Ready] },
        dataKey: 'items',
        axiosOptions: reqOpts,
      })

  const sellerAccountPromise =
    isAuctionListing || !sellerId
      ? null
      : accountsClient.getAccount({ id: sellerId }, reqOpts)

  const storefrontPromise = storefrontsClient.findOneStoreFront(
    { id: storefrontId },
    reqOpts
  )

  const [
    auction,
    auctionItemsResponse,
    listing,
    lotResponse,
    offerings,
    offeringItems,
    offersResponse,
    sellerAccountResponse,
    storefront,
  ] = await Promise.all([
    auctionPromise,
    auctionItemsPromise,
    listingPromise,
    lotPromise,
    offeringsPromise,
    offeringItemsPromise,
    offersPromise,
    sellerAccountPromise,
    storefrontPromise,
  ])

  let items: ItemDto[] = []
  let itemsTotal: number = 0

  if (auctionItemsResponse) {
    items = auctionItemsResponse.data.items
    itemsTotal = auctionItemsResponse.data.total
  } else if (offeringItems) {
    items = offeringItems
    itemsTotal = items.length
  }

  const offering = offerings?.at(0) ?? null
  const offers = offersResponse?.data.offers ?? null

  const sellerAccount = sellerAccountResponse?.data ?? null

  /**
   * Step 2: fetches which depends on step 1 responses
   */
  // Could be step 1 if we add searchListing.contractId
  const contractPromise = fetchContractById(listing, accessToken)

  const groupsPromise = groupsClient.findAllGroups(
    { id: lotResponse.data.groups },
    reqOpts
  )

  // Fetch lot locations
  const augmentedLotsPromise = augmentLots([lotResponse.data], accessToken)

  const { termsAcceptedPromise, termsErrorHandler, termsMetadataPromise } =
    buildTermsPromises({
      accessToken,
      termsRestrictions: listing.termsRestrictions,
    })

  const [
    contract,
    augmentedLots,
    groupsResponse,
    [termsAcceptedResponse, termsMetadataResponse],
  ] = await Promise.all([
    contractPromise,
    augmentedLotsPromise,
    groupsPromise,
    Promise.all([termsAcceptedPromise, termsMetadataPromise]).catch(
      termsErrorHandler
    ),
  ])

  const orchestratedLot = augmentedLots[0]
  if (!orchestratedLot) {
    throw new Error('Unable to augment lot with location information')
  }
  const { groups } = groupsResponse.data
  const lot: OrchestratedLotWithGroups = { ...orchestratedLot, groups }

  const acceptedPolicies = termsAcceptedResponse?.data ?? null
  const termsMetadata = termsMetadataResponse?.data[0] ?? null

  const commonListingDetails: ListingDetailsCommon = {
    ...listing,
    acceptedPolicies,
    contract,
    deal,
    itemsTotal,
    lot,
    problemFreeOrderRate,
    seller: {
      storefront: storefront.data,
      account: getSellerInfo(isAuctionListing ? auction : sellerAccount),
    },
    termsMetadata,
    typicalSellingPrice: typicalSellingPrice,
  }

  if (isAuctionListing) {
    if (!auction) throw new Error('Failed to fetch auction for auction listing')

    const result: ListingDetailsCommon & OrchestratedAuctionListingDetails = {
      ...commonListingDetails,
      auction,
      inventoryPreview: items,
      items: null,
      offering: null,
      offers: null,
    }
    return result
  }

  const result: ListingDetailsCommon & OrchestratedOfferingListingDetails = {
    ...commonListingDetails,
    auction: null,
    inventoryPreview: null,
    items: augmentOfferingItems(items),
    offering,
    offers,
  }
  return result
}

/**
 * Query orchestrated data for a listing given a CombinedListingSearchResult.
 */
const searchListingDetailsQuery = makeOptionallyAuthenticatedQuery(
  'searchListingDetails',
  getSearchListingDetails,
  {
    makeKey: (name, [accessToken, searchListing]) => [
      name,
      searchListing.id,
      accessToken ? sanitizeAccessToken(accessToken) : null,
    ],
  }
)

export { getSearchListingDetails }
export default searchListingDetailsQuery
